import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Calendar, MapPin, Users, Phone, Mail } from 'lucide-react';

const Gulfood = () => {
  return (
    <div className="w-full">
      {/* Hero */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl font-bold mb-6">Meet SPANXPORT at Gulfood 2026</h1>
            <div className="flex items-center justify-center text-2xl text-blue-600 mb-6">
              <Calendar className="w-8 h-8 mr-3" />
              <span className="font-semibold">26-30 January 2026 | Dubai</span>
            </div>
            <p className="text-xl text-slate-700">
              Book a 15-minute meeting to discuss your commodity requirement and documentation needs.
            </p>
          </div>
        </div>
      </section>

      {/* Event Details */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              <Card className="p-6 text-center">
                <Calendar className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">When</h3>
                <p className="text-slate-600">26-30 January 2026</p>
                <p className="text-sm text-slate-500 mt-2">5 days of networking</p>
              </Card>
              <Card className="p-6 text-center">
                <MapPin className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Where</h3>
                <p className="text-slate-600">Dubai, UAE</p>
                <p className="text-sm text-slate-500 mt-2">Gulfood Exhibition</p>
              </Card>
              <Card className="p-6 text-center">
                <Users className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Who</h3>
                <p className="text-slate-600">SPANXPORT Team</p>
                <p className="text-sm text-slate-500 mt-2">Available for meetings</p>
              </Card>
            </div>

            <Card className="p-8 bg-blue-50 border-blue-200">
              <h2 className="text-3xl font-bold mb-4 text-center">Why Meet Us?</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-xl font-semibold mb-3">For Buyers</h3>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      <span>Discuss your commodity requirements</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      <span>Learn about EximStack documentation system</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      <span>Review quality assurance processes</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      <span>Explore repeat lane opportunities</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-3">For Sellers</h3>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      <span>Join our Verified Directory network</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      <span>Access international buyer network</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      <span>Build credibility through verification</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      <span>Scale your export operations</span>
                    </li>
                  </ul>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Meeting Topics */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-12 text-center">What We Can Discuss</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-3">Commodity Supply</h3>
                <p className="text-slate-600 text-sm">
                  Spices, edible oils, sugar, grains, and fresh/preserved produce with specifications and pricing
                </p>
              </Card>
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-3">EximStack System</h3>
                <p className="text-slate-600 text-sm">
                  Traceability, QA gates, and export documentation features for buyer-ready shipments
                </p>
              </Card>
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-3">Verified Directory</h3>
                <p className="text-slate-600 text-sm">
                  Join our international network of verified buyers and sellers for repeat trade lanes
                </p>
              </Card>
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-3">Market Entry</h3>
                <p className="text-slate-600 text-sm">
                  Documentation requirements and logistics for your target markets (Africa, Asia-Pacific, GCC, LATAM, South Asia, UK & EU, US & Canada)
                </p>
              </Card>
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-3">Private Label</h3>
                <p className="text-slate-600 text-sm">
                  Custom blends, packaging, and branding support for retail and distributor partners
                </p>
              </Card>
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-3">Partnership Models</h3>
                <p className="text-slate-600 text-sm">
                  Explore wholesale, distribution, and exclusive lane partnership opportunities
                </p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Book a Meeting */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="p-10 bg-gradient-to-br from-blue-900 to-blue-700 text-white">
              <div className="text-center">
                <h2 className="text-4xl font-bold mb-4">Book Your Meeting Now</h2>
                <p className="text-xl mb-8 opacity-90">
                  Limited slots available. Reserve your 15-minute consultation at Gulfood 2026.
                </p>
                <div className="space-y-4 mb-8">
                  <div className="flex items-center justify-center">
                    <Mail className="w-5 h-5 mr-3" />
                    <a href="mailto:admin@spanxport.com" className="text-lg hover:underline">
                      admin@spanxport.com
                    </a>
                  </div>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
                    <div className="flex items-center">
                      <Phone className="w-5 h-5 mr-2" />
                      <span className="text-lg">WhatsApp:</span>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <a href="https://wa.me/918291234503" className="hover:underline">
                        +91-829-12345-03
                      </a>
                      <span className="hidden sm:inline">|</span>
                      <a href="https://wa.me/6598635879" className="hover:underline">
                        +65-986-35879
                      </a>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button asChild size="lg" className="bg-white text-blue-900 hover:bg-gray-100">
                    <a href="https://meetings-na2.hubspot.com/pmonga" target="_blank" rel="noopener noreferrer">
                      Book a Meeting
                    </a>
                  </Button>
                  <Button
                    asChild
                    size="lg"
                    variant="outline"
                    className="border-white text-white hover:bg-white/10"
                  >
                    <a
                      href="https://wa.me/918291234503?text=Hello%20SPANXPORT%20-%20I%20want%20to%20book%20a%20meeting%20at%20Gulfood%202026"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      WhatsApp Us
                    </a>
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Preparation Tips */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-center">Prepare for the Meeting</h2>
            <Card className="p-8">
              <p className="text-lg text-slate-700 mb-6">
                To make the most of our meeting, please bring or prepare:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="text-blue-600 mr-3 font-bold text-lg">✓</span>
                  <span className="text-slate-700">Commodity type, specifications, and quality requirements</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-3 font-bold text-lg">✓</span>
                  <span className="text-slate-700">Target quantity and delivery schedule</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-3 font-bold text-lg">✓</span>
                  <span className="text-slate-700">Destination countries and ports</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-3 font-bold text-lg">✓</span>
                  <span className="text-slate-700">Required certifications and documentation needs</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-3 font-bold text-lg">✓</span>
                  <span className="text-slate-700">Company profile and current supply challenges (if any)</span>
                </li>
              </ul>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Gulfood;
